import { useTranslation } from '@/contexts/i18n-context';
import { Form } from '@/components/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import styles from './styles.module.css';
import { CreateAllergyParams } from '@/api/api.schemas';
import { requiredString } from '@/util/validators';
import { AllergyIcon } from '../icons/allergy-icon';
import { Expander } from '@/components/expander';
import { useGetAllergies } from '@/api/get-allergies';
import { SearchableSelect } from '@/components/inputs/searchable-select';
import { InputLabel } from '@/components/inputs/input-label';
import { MedicalHistoryItemList } from '../medical-history-item-list';

const allergySchema = z.object({
  externalId: requiredString(),
});

type FormValues = z.infer<typeof allergySchema>;

interface AllergyOption {
  name: string;
  allergenId: string;
  value: string;
  label: string;
}

interface AllergiesFormProps {
  onRemove: (id: string) => void;
  onAdd: (allergy: CreateAllergyParams) => void;
  allergies: CreateAllergyParams[];
  isOpen?: boolean;
  onExpanderClick?: () => void;
}

const translationBase = 'visit.medicalHistory.allergiesForm';

export function AllergiesForm({
  onRemove,
  onAdd,
  allergies,
  isOpen = false,
  onExpanderClick,
}: AllergiesFormProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const form = useForm<FormValues>({
    resolver: zodResolver(allergySchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      externalId: '',
    },
  });

  const enableSearch = searchTerm.trim().length > 1;

  const { data: selectAllergies, isFetching } = useGetAllergies(
    { searchTerm },
    { query: { enabled: enableSearch } },
  );

  const allergiesNames = allergies.map((allergy) => allergy.text);
  const selectAllergiesToShow = selectAllergies?.allergies?.filter(
    (allergy) => !allergiesNames.includes(allergy.name),
  );

  const options: AllergyOption[] =
    selectAllergiesToShow?.map((allergy) => ({
      ...allergy,
      value: allergy.allergenId,
      label: allergy.name,
    })) ?? [];

  return (
    <Expander
      icon={<AllergyIcon className={styles.icon} />}
      title={t(`${translationBase}.title`)}
      indicatorText={t(`${translationBase}.indicatorText`)}
      collapsedText={allergies.map((allergy) => allergy.text).join(', ')}
      isOpen={isOpen}
      onClick={onExpanderClick}
    >
      <Form form={form} className={styles.form}>
        <div className={styles.formContainer}>
          <InputLabel label={t(`${translationBase}.allergyLabel`)} htmlFor="externalId" />
          <SearchableSelect
            className={styles.input}
            name="externalId"
            options={options}
            placeholder={t(`${translationBase}.allergyPlaceholder`)}
            onSearch={setSearchTerm}
            isLoading={isFetching}
            menuIsOpen={enableSearch}
            onChange={(value) => {
              onAdd({
                externalId: value.value,
                text: value.name,
              });
            }}
          />
        </div>
        <MedicalHistoryItemList
          items={allergies.map((allergy) => ({
            id: allergy.externalId,
            text: allergy.text,
          }))}
          onRemove={onRemove}
        />
      </Form>
    </Expander>
  );
}
